import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, ShieldCheck } from "lucide-react";
import { tzOpts } from "@/lib/tz";

const KIND = {
  cobranca_cancelada: "Cobrança cancelada",
  baixa_pendente: "Baixa pendente no mk-auth",
  pagamento_duplicado: "Pagamento duplicado",
  baixa_efi_falhou: "Baixa do boleto na EFI falhou",
};

export default function AuditCard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/audit/latest");
      setReport(data.report);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/audit/run", {}, { timeout: 180000 });
      setReport(data.report);
      toast.success("Conferência concluída");
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setRunning(false);
    }
  };

  const fixed = report?.fixed || [];
  const att = report?.attention || [];
  const when = report?.at ? new Date(report.at).toLocaleString("pt-BR", { ...tzOpts(), dateStyle: "short", timeStyle: "short" }) : null;

  return (
    <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4" data-testid="audit-card">
      <div className="flex items-center justify-between gap-3 flex-wrap border-b border-slate-800 pb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-400" />
          <div>
            <div className="text-white font-semibold">Conferência diária</div>
            <div className="text-xs text-slate-500">
              Compara mk-auth, Quitaí e os bancos todo dia cedo; corrige o que é seguro e avisa o resto.
              {when && <> Última: {when}.</>}
            </div>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={run} disabled={running} data-testid="audit-run-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${running ? "animate-spin" : ""}`} />
          {running ? "Conferindo…" : "Conferir agora"}
        </Button>
      </div>

      {loading && <div className="text-sm text-slate-500">Carregando…</div>}
      {!loading && !report && <div className="text-sm text-slate-500">Ainda não rodou. Clique em “Conferir agora”.</div>}

      {report && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">{fixed.length} corrigida(s) automaticamente</Badge>
            <Badge className={att.length ? "bg-amber-500/10 text-amber-300 border-amber-500/30" : "bg-slate-800 text-slate-400 border-slate-700"}>
              {att.length} precisa(m) de atenção
            </Badge>
          </div>

          {att.length > 0 && (
            <div className="space-y-1.5" data-testid="audit-attention">
              <div className="text-xs uppercase tracking-wider text-amber-400">Precisa de atenção</div>
              {att.map((a, i) => (
                <div key={i} className="text-sm text-slate-300 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2">
                  <span className="font-semibold text-white">{a.name || a.login || `Boleto ${a.title_id}`}</span>
                  <span className="text-slate-500"> · boleto {a.title_id} · R$ {a.amount}</span>
                  <div className="text-xs text-amber-300">{KIND[a.kind] || a.kind}: {a.reason}</div>
                </div>
              ))}
            </div>
          )}

          {fixed.length > 0 && (
            <details data-testid="audit-fixed">
              <summary className="text-xs uppercase tracking-wider text-emerald-400 cursor-pointer">
                Corrigidas automaticamente ({fixed.length})
              </summary>
              <div className="mt-2 space-y-1.5">
                {fixed.map((a, i) => (
                  <div key={i} className="text-sm text-slate-400 px-3 py-1.5 rounded-md bg-slate-800/40">
                    <span className="text-slate-200">{a.name || a.login || `Boleto ${a.title_id}`}</span>
                    <span className="text-slate-500"> · boleto {a.title_id} · R$ {a.amount} · {a.provider}</span>
                    <div className="text-xs text-slate-500">{a.reason}{a.provider_result ? ` · ${a.provider_result}` : ""}</div>
                  </div>
                ))}
              </div>
            </details>
          )}

          {fixed.length === 0 && att.length === 0 && (
            <div className="text-sm text-emerald-300">Tudo certo: nada para corrigir.</div>
          )}
        </div>
      )}
    </Card>
  );
}
